import { create } from 'zustand';
import { Room, RoomState, BroadcastLink, RoomMemberRole, ApiKey } from '../types';
import { RoomService } from '../services/roomService';
import { ApiKeyService } from '../services/apiKeyService';
import { supabase } from '../lib/supabase';

type RoomStore = {
  rooms: Room[];
  loading: boolean;
  error: string | null;
  lastProfileUpdate: number;
  loadRooms: (userId: string) => Promise<void>;
  refreshRoomUserProfiles: (roomId: string) => Promise<void>;
  addRoom: (room: Omit<Room, 'id' | 'lastUpdated' | 'lastUpdatedBy' | 'broadcastLinks'>, createdBy: string) => Promise<void>;
  updateRoomState: (roomId: string, stateId: string, userId: string) => Promise<void>;
  inviteUserToRoom: (roomId: string, email: string, role?: RoomMemberRole, message?: string) => Promise<void>;
  removeUserFromRoom: (roomId: string, userId: string) => Promise<void>;
  leaveRoom: (roomId: string, userId: string) => Promise<void>;
  updateUserRole: (roomId: string, userId: string, role: RoomMemberRole) => Promise<void>;
  updateRoomSettings: (roomId: string, settings: Partial<Pick<Room, 'size' | 'viewMode'>>) => Promise<void>;
  updateRoomTimeoutSettings: (roomId: string, settings: { autoResetState?: { stateId: string; afterMinutes: number } }) => Promise<void>;
  updateRoomStates: (roomId: string, states: RoomState[]) => Promise<void>;
  createBroadcastLink: (roomId: string, options?: { name?: string; timeoutMinutes?: number; expiresAt?: Date }) => Promise<void>;
  updateBroadcastLink: (roomId: string, linkId: string, updates: { name?: string; active?: boolean; timeoutMinutes?: number | null; expiresAt?: Date | null }) => Promise<void>;
  revokeBroadcastLink: (roomId: string, linkId: string) => Promise<void>;
  deleteBroadcastLink: (roomId: string, linkId: string) => Promise<void>;
  getRoomByBroadcastLink: (roomId: string, linkId: string) => Promise<Room | null>;
  // API Key methods
  createApiKey: (roomId: string, keyData: {
    name: string;
    permissions: { read: boolean; write: boolean; };
    expiresAt?: Date;
    rateLimit?: { requestsPerMinute: number; requestsPerHour: number; };
    webhookUrl?: string;
  }) => Promise<ApiKey>;
  deleteApiKey: (keyId: string) => Promise<void>;
  toggleApiKey: (keyId: string, active: boolean) => Promise<void>;
  loadRoomApiKeys: (roomId: string, userId: string) => Promise<void>;
};

export const useRoomStore = create<RoomStore>((set, get) => ({
  rooms: [],
  loading: false,
  error: null,
  lastProfileUpdate: 0,

  loadRooms: async (userId: string) => {
    set({ loading: true, error: null });
    try {
      const rooms = await RoomService.getUserRooms(userId);
      
      // Load API keys for each room
      const roomsWithApiKeys = await Promise.all(
        rooms.map(async (room) => {
          try {
            const apiKeys = await ApiKeyService.getRoomApiKeys(room.id, userId);
            return { ...room, apiKeys };
          } catch (error) {
            console.error(`Failed to load API keys for room ${room.id}:`, error);
            return { ...room, apiKeys: [] };
          }
        })
      );
      
      set({ rooms: roomsWithApiKeys, loading: false });
    } catch (error) {
      console.error('Failed to load rooms:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to load rooms', loading: false });
    }
  },

  refreshRoomUserProfiles: async (roomId: string) => {
    try {
      // Get current timestamp
      const now = Date.now();
      
      // Check if we've updated this room recently (within 2 seconds)
      if (get().lastProfileUpdate && now - get().lastProfileUpdate < 2000) {
        console.log('Skipping profile refresh - too soon after last update');
        return;
      }

      // Update the last update timestamp
      set({ lastProfileUpdate: now });

      const updatedProfiles = await RoomService.refreshRoomUserProfiles(roomId);
      
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? { ...room, users: updatedProfiles }
            : room
        )
      }));
    } catch (error) {
      console.error('Failed to refresh room user profiles:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to refresh user profiles' });
      throw error; // Re-throw to handle in UI
    }
  },

  addRoom: async (roomData, createdBy) => {
    set({ loading: true, error: null });
    try {
      const newRoom = await RoomService.createRoom(roomData, createdBy);
      set(state => ({ 
        rooms: [...state.rooms, { ...newRoom, apiKeys: [] }], 
        loading: false 
      }));
    } catch (error) {
      console.error('Failed to create room:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to create room', loading: false });
    }
  },

  updateRoomState: async (roomId, stateId, userId) => {
    try {
      await RoomService.updateRoomState(roomId, stateId, userId);
      
      set(state => ({
        rooms: state.rooms.map(room => 
          room.id === roomId
            ? {
                ...room,
                currentState: room.states.find(s => s.id === stateId) || room.currentState,
                lastUpdated: Date.now(),
                lastUpdatedBy: userId
              }
            : room
        )
      }));
    } catch (error) {
      console.error('Failed to update room state:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update room state' });
    }
  },

  inviteUserToRoom: async (roomId, email, role = 'USER', message) => {
    try {
      await RoomService.inviteUserToRoom(roomId, email, role, message);
    } catch (error) {
      console.error('Failed to invite user to room:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to invite user to room' });
      throw error;
    }
  },

  removeUserFromRoom: async (roomId, userId) => {
    try {
      await RoomService.removeUserFromRoom(roomId, userId);
      
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? { ...room, users: room.users.filter(u => u.id !== userId) }
            : room
        )
      }));
    } catch (error) {
      console.error('Failed to remove user from room:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to remove user from room' });
    }
  },

  leaveRoom: async (roomId: string, userId: string) => {
    try {
      // Get the current room
      const room = get().rooms.find(r => r.id === roomId);
      if (!room) return;

      // Check if this is the last user
      const isLastUser = room.users.length === 1;
      const isAdmin = room.users.find(u => u.id === userId)?.role === 'ADMIN';

      // If this is the last user and they're an admin, delete the room
      if (isLastUser && isAdmin) {
        const { error } = await supabase
          .from('rooms')
          .delete()
          .eq('id', roomId);

        if (error) throw error;
      } else {
        // Otherwise just remove the user
        await RoomService.removeUserFromRoom(roomId, userId);
      }
      
      // Remove the room from the store
      const currentRooms = get().rooms;
      set({ rooms: currentRooms.filter(room => room.id !== roomId) });
    } catch (error) {
      console.error('Failed to leave room:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to leave room' });
      throw error;
    }
  },

  updateUserRole: async (roomId, userId, role) => {
    try {
      console.log(`Updating user ${userId} role to ${role} in room ${roomId}`);
      await RoomService.updateUserRole(roomId, userId, role);
      
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? {
                ...room,
                users: room.users.map(user =>
                  user.id === userId ? { ...user, role } : user
                )
              }
            : room
        )
      }));
      
      console.log('Role update successful');
    } catch (error) {
      console.error('Failed to update user role:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update user role' });
      throw error; // Re-throw to handle in UI
    }
  },

  updateRoomSettings: async (roomId, settings) => {
    try {
      await RoomService.updateRoomSettings(roomId, settings);
      
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? { ...room, ...settings }
            : room
        )
      }));
    } catch (error) {
      console.error('Failed to update room settings:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update room settings' });
    }
  },

  updateRoomTimeoutSettings: async (roomId, settings) => {
    try {
      await RoomService.updateRoomTimeoutSettings(roomId, settings);
      
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? { ...room, autoResetState: settings.autoResetState }
            : room
        )
      }));
    } catch (error) {
      console.error('Failed to update room timeout settings:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update room timeout settings' });
    }
  },

  updateRoomStates: async (roomId, states) => {
    try {
      await RoomService.updateRoomStates(roomId, states);
      
      // After successful update, fetch the updated room states
      const { data: updatedStates, error: fetchError } = await supabase
        .from('room_states')
        .select('*')
        .eq('room_id', roomId);
        
      if (fetchError) throw fetchError;
      
      // Map the updated states to our format
      const formattedStates = updatedStates.map(state => ({
        id: state.id,
        name: state.name,
        color: state.color,
        emoji: state.emoji
      }));
      
      // Update the store with the fresh states from the database
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? { 
                ...room, 
                states: formattedStates,
                // Update current state if it exists in the new states
                currentState: formattedStates.find(s => s.id === room.currentState.id) || 
                              formattedStates[0] || 
                              room.currentState
              }
            : room
        )
      }));
    } catch (error) {
      console.error('Failed to update room states:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update room states' });
      throw error;
    }
  },

  createBroadcastLink: async (roomId, options = {}) => {
    try {
      const newLink = await RoomService.createBroadcastLink(roomId, options);
      
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? {
                ...room,
                broadcastLinks: [...room.broadcastLinks, newLink]
              }
            : room
        )
      }));
    } catch (error) {
      console.error('Failed to create broadcast link:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to create broadcast link' });
    }
  },

  updateBroadcastLink: async (roomId, linkId, updates) => {
    try {
      await RoomService.updateBroadcastLink(roomId, linkId, updates);
      
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? {
                ...room,
                broadcastLinks: room.broadcastLinks.map(link =>
                  link.id === linkId 
                    ? { 
                        ...link, 
                        name: updates.name !== undefined ? updates.name : link.name,
                        active: updates.active !== undefined ? updates.active : link.active,
                        timeoutMinutes: updates.timeoutMinutes !== undefined ? updates.timeoutMinutes : link.timeoutMinutes,
                        expiresAt: updates.expiresAt !== undefined 
                          ? (updates.expiresAt ? updates.expiresAt.getTime() : undefined) 
                          : link.expiresAt
                      } 
                    : link
                )
              }
            : room
        )
      }));
    } catch (error) {
      console.error('Failed to update broadcast link:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update broadcast link' });
    }
  },

  revokeBroadcastLink: async (roomId, linkId) => {
    try {
      await RoomService.revokeBroadcastLink(roomId, linkId);
      
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? {
                ...room,
                broadcastLinks: room.broadcastLinks.map(link =>
                  link.id === linkId ? { ...link, active: false } : link
                )
              }
            : room
        )
      }));
    } catch (error) {
      console.error('Failed to revoke broadcast link:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to revoke broadcast link' });
    }
  },

  deleteBroadcastLink: async (roomId, linkId) => {
    try {
      await RoomService.deleteBroadcastLink(roomId, linkId);
      
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? {
                ...room,
                broadcastLinks: room.broadcastLinks.filter(link => link.id !== linkId)
              }
            : room
        )
      }));
    } catch (error) {
      console.error('Failed to delete broadcast link:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to delete broadcast link' });
    }
  },

  getRoomByBroadcastLink: async (roomId, linkId) => {
    try {
      return await RoomService.getRoomByBroadcastLink(roomId, linkId);
    } catch (error) {
      console.error('Failed to get room by broadcast link:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to get room' });
      return null;
    }
  },

  // API Key methods
  createApiKey: async (roomId, keyData) => {
    try {
      const state = get();
      const room = state.rooms.find(r => r.id === roomId);
      if (!room) throw new Error('Room not found');

      // Get current user ID from auth store (you might need to pass this as parameter)
      const userId = room.users[0]?.id; // This is a simplification - in real app, get from auth
      if (!userId) throw new Error('User not found');

      const newApiKey = await ApiKeyService.createApiKey(userId, roomId, keyData);
      
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? {
                ...room,
                apiKeys: [...(room.apiKeys || []), newApiKey]
              }
            : room
        )
      }));

      return newApiKey;
    } catch (error) {
      console.error('Failed to create API key:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to create API key' });
      throw error;
    }
  },

  deleteApiKey: async (keyId) => {
    try {
      await ApiKeyService.deleteApiKey(keyId);
      
      set(state => ({
        rooms: state.rooms.map(room => ({
          ...room,
          apiKeys: (room.apiKeys || []).filter(key => key.id !== keyId)
        }))
      }));
    } catch (error) {
      console.error('Failed to delete API key:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to delete API key' });
      throw error;
    }
  },

  toggleApiKey: async (keyId, active) => {
    try {
      await ApiKeyService.updateApiKey(keyId, { active });
      
      set(state => ({
        rooms: state.rooms.map(room => ({
          ...room,
          apiKeys: (room.apiKeys || []).map(key =>
            key.id === keyId ? { ...key, active } : key
          )
        }))
      }));
    } catch (error) {
      console.error('Failed to toggle API key:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to toggle API key' });
      throw error;
    }
  },

  loadRoomApiKeys: async (roomId, userId) => {
    try {
      const apiKeys = await ApiKeyService.getRoomApiKeys(roomId, userId);
      
      set(state => ({
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? { ...room, apiKeys }
            : room
        )
      }));
    } catch (error) {
      console.error('Failed to load room API keys:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to load API keys' });
    }
  }
}));